import { useEffect, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Smartphone, Monitor, Tablet, Laptop, LogOut, Clock, MapPin, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

function deviceIcon(device = "") {
  if (device.includes("Android") || device.includes("iOS")) return Smartphone;
  if (device.includes("macOS") || device.includes("Linux")) return Laptop;
  return Monitor;
}

function relTime(iso) {
  if (!iso) return "—";
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `hace ${Math.floor(sec)}s`;
  if (sec < 3600) return `hace ${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `hace ${Math.floor(sec / 3600)}h`;
  return `hace ${Math.floor(sec / 86400)}d`;
}

export default function Sessions() {
  const { user, logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const revoke = async (s) => {
    if (!window.confirm(`¿Cerrar sesión en ${s.device}?${s.is_current ? " (esto te desconectará)" : ""}`)) return;
    setRevoking(s.id);
    try {
      await api.delete(`/auth/sessions/${s.id}`);
      if (s.is_current) {
        toast.success("Sesión cerrada");
        await logout();
        window.location.href = "/login";
        return;
      }
      toast.success("Sesión revocada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setRevoking(null);
  };

  const revokeAllOthers = async () => {
    const others = sessions.filter((s) => !s.is_current && !s.revoked_at);
    if (others.length === 0) return toast.info("No hay otras sesiones activas");
    if (!window.confirm(`¿Cerrar ${others.length} sesión(es) en otros dispositivos?`)) return;
    try {
      await Promise.all(others.map((s) => api.delete(`/auth/sessions/${s.id}`)));
      toast.success(`${others.length} sesión(es) cerradas`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const active = sessions.filter((s) => !s.revoked_at);
  const recent = sessions.filter((s) => s.revoked_at).slice(0, 10);

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Mis Sesiones"
        subtitle="Dispositivos donde tu cuenta está conectada"
        testId="sessions-header"
        actions={
          active.length > 1 ? (
            <Button onClick={revokeAllOthers} variant="outline" className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 font-semibold" data-testid="revoke-others">
              <ShieldAlert className="w-4 h-4 mr-2" /> Cerrar otros dispositivos
            </Button>
          ) : null
        }
      />

      <div className="p-6 sm:p-8 max-w-3xl space-y-6">
        {loading ? (
          <div className="text-slate-500 text-sm">Cargando…</div>
        ) : (
          <>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">
                Activas ({active.length})
              </div>
              <div className="space-y-2">
                {active.map((s) => {
                  const Icon = deviceIcon(s.device);
                  return (
                    <div
                      key={s.id}
                      className={`bg-white border-2 rounded-sm p-4 flex items-start gap-4 ${s.is_current ? "border-green-500" : "border-slate-200"}`}
                      data-testid={`session-${s.id}`}
                    >
                      <div className={`w-11 h-11 rounded-sm flex items-center justify-center shrink-0 ${s.is_current ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-bold text-slate-950">{s.device || "Desconocido"}</div>
                          {s.is_current && (
                            <span className="text-[10px] font-black uppercase tracking-widest bg-green-600 text-white px-2 py-0.5 rounded-sm">
                              Este dispositivo
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600 font-mono">
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.ip || "—"}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Última actividad: {relTime(s.last_seen)}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1 truncate" title={s.user_agent}>
                          {s.user_agent}
                        </div>
                      </div>
                      <Button
                        onClick={() => revoke(s)}
                        disabled={revoking === s.id}
                        variant="outline"
                        size="sm"
                        className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 shrink-0"
                        data-testid={`revoke-${s.id}`}
                      >
                        <LogOut className="w-3 h-3 mr-1" />
                        {revoking === s.id ? "..." : "Cerrar"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            {recent.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">
                  Recientes cerradas
                </div>
                <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100">
                  {recent.map((s) => (
                    <div key={s.id} className="px-4 py-2 text-xs flex items-center justify-between text-slate-500 font-mono">
                      <span>{s.device}</span>
                      <span>Cerrada {relTime(s.revoked_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
